/**
 * CallbackButton: "Call back" on a call's detail (docs/plans/oncall-line-plan.md
 * 6.2, 7.7; WP-14).
 *
 * Shown only when the server says the viewer may use it (OncallCallDetail
 * .can_callback: an inbound call on the site's active line, and the viewer is
 * an active member of that line linked to their user). Pressing it opens a
 * confirm step that explains what happens and offers the number to call:
 * the one the participant called from (named by its last four digits only),
 * or a different US number for a caller who asked to be called somewhere
 * else. Confirming posts POST /calls/:id/callback (api/oncallApi.ts
 * startCallback): SiteOps rings the member's own cell from the line's
 * number, and pressing 1 there dials the participant from the line's number,
 * so the participant never sees a staff cell.
 *
 * The server holds every guardrail (the callback window, US numbers only with
 * 900 refused, the daily cap, the audit row, the dial guard); the checks here
 * only save a round trip, and a refusal is shown in the dialog in the
 * server's words (a few codes get plainer ones). The contract with WP-12's
 * drawer is the props below and both exports.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { AlertCircle, CheckCircle2, Loader2, PhoneOutgoing } from 'lucide-react';
import Modal from '../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../ui/styles';
import { apiErrorCode, apiErrorMessage, startCallback } from '../api/oncallApi';
import type { OncallCallDetail, OncallCallbackResult } from '../api/oncallApi';

export interface CallbackButtonProps {
  /** The call whose caller is rung back (its detail, as GET /calls/:id answers it). */
  call: OncallCallDetail;
  /** Called once the bridge call is placed, so the drawer can refresh. */
  onPlaced?: (result: OncallCallbackResult) => void;
}

type Target = 'original' | 'new';

/**
 * A US (NANP) number as E.164, or null: ten digits (or eleven starting with
 * 1), the area code and the exchange each starting 2 to 9. Mirrors the
 * server's line.normalizeNanp closely enough to catch typos before a request.
 */
export function normalizeUsNumber(raw: string): string | null {
  if (typeof raw !== 'string' || /[a-z]/i.test(raw)) return null;
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(ten) ? `+1${ten}` : null;
}

/** Why a typed number cannot be called back, or null when it can. */
export function numberProblem(raw: string): string | null {
  if (!raw.trim()) return 'Enter the number to call.';
  const e164 = normalizeUsNumber(raw);
  if (!e164) return 'Enter a 10-digit US phone number.';
  if (e164.startsWith('+1900')) return '900 numbers cannot be called.';
  return null;
}

/** Plainer words for refusals whose server message is written for admins. */
const FRIENDLY: Record<string, string> = {
  ONCALL_VOICE_DISABLED: "The 24-Hour Line is switched off, so a callback can't be placed right now.",
  ONCALL_CALLBACK_IN_PROGRESS: 'A callback to your phone is already ringing. Answer it, or try again in a minute.',
  ONCALL_DIAL_REFUSED_NOT_PRODUCTION: 'Calls are switched off in this environment.',
  ONCALL_DIAL_OVERRIDE_INVALID: 'Calls are switched off in this environment.',
  ONCALL_TWILIO_FAILED: "The phone service didn't place the call. Try again.",
  ONCALL_TWILIO_UNCONFIGURED: 'The phone service is not set up yet. Ask a line admin.',
  ONCALL_PUBLIC_API_URL_UNSET: 'The phone service is not set up yet. Ask a line admin.',
  ONCALL_PUBLIC_API_URL_INVALID: 'The phone service is not set up yet. Ask a line admin.',
};

const radioRowCls = 'flex min-h-[44px] cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors duration-150 sm:min-h-0';
const radioOn = 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20';
const radioOff = 'border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50';
const radioInputCls = 'mt-0.5 h-4 w-4 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-700';

export function CallbackButton({ call, onPlaced }: CallbackButtonProps) {
  const hasOriginal = Boolean(call.caller_last4);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Target>(hasOriginal ? 'original' : 'new');
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState(false);

  // A different call in the drawer starts fresh.
  useEffect(() => {
    setOpen(false);
    setPlaced(false);
    setError(null);
  }, [call.id]);

  const openDialog = useCallback(() => {
    setTarget(hasOriginal ? 'original' : 'new');
    setNumber('');
    setError(null);
    setPlaced(false);
    setOpen(true);
  }, [hasOriginal]);

  const close = useCallback(() => {
    if (!busy) setOpen(false);
  }, [busy]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (target === 'new') {
      const problem = numberProblem(number);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      const result = await startCallback(call.id, target === 'new' ? { number: normalizeUsNumber(number) as string } : {});
      setBusy(false);
      setOpen(false);
      setPlaced(true);
      onPlaced?.(result);
    } catch (err) {
      setBusy(false);
      const code = apiErrorCode(err);
      setError((code && FRIENDLY[code]) || apiErrorMessage(err, 'The callback could not be placed. Try again.'));
    }
  }

  if (call.can_callback !== true || call.direction !== 'inbound') return null;

  return (
    <div data-testid="oncall-callback">
      <button type="button" className={primaryBtnCls} onClick={openDialog} data-testid="oncall-callback-button">
        <PhoneOutgoing className="h-4 w-4" aria-hidden="true" />
        Call back
      </button>
      <div aria-live="polite">
        {placed && (
          <p
            role="status"
            data-testid="oncall-callback-placed"
            className="mt-2 flex items-start gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Calling your phone now. Answer it and press 1 to connect to the caller.</span>
          </p>
        )}
      </div>

      <Modal isOpen={open} onClose={close} title="Call back this caller" maxWidth="max-w-md">
        <form onSubmit={submit} className="space-y-4" data-testid="oncall-callback-confirm" noValidate>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            SiteOps will ring your own phone from the 24-hour line&apos;s number. Answer it and press 1 to be
            connected. The caller sees the 24-hour line&apos;s number, never yours, and the call is transcribed.
          </p>

          <fieldset className="space-y-2">
            <legend className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Number to call
            </legend>
            <label className={`${radioRowCls} ${target === 'original' ? radioOn : radioOff} ${hasOriginal ? '' : 'cursor-not-allowed opacity-60'}`}>
              <input
                type="radio"
                name={`oncall-callback-target-${call.id}`}
                value="original"
                checked={target === 'original'}
                disabled={!hasOriginal || busy}
                onChange={() => { setTarget('original'); setError(null); }}
                className={radioInputCls}
                data-testid="oncall-callback-original"
              />
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 dark:text-gray-100">The number they called from</span>
                <span className="block text-xs text-gray-500 tabular-nums dark:text-gray-400">
                  {hasOriginal ? `Ending in ${call.caller_last4}` : 'Not on record for this call'}
                </span>
              </span>
            </label>
            <label className={`${radioRowCls} ${target === 'new' ? radioOn : radioOff}`}>
              <input
                type="radio"
                name={`oncall-callback-target-${call.id}`}
                value="new"
                checked={target === 'new'}
                disabled={busy}
                onChange={() => { setTarget('new'); setError(null); }}
                className={radioInputCls}
                data-testid="oncall-callback-new"
              />
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 dark:text-gray-100">A different US number</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">For a caller who asked to be called somewhere else</span>
              </span>
            </label>
            {target === 'new' && (
              <div>
                <label htmlFor={`oncall-callback-number-${call.id}`} className="sr-only">Phone number to call</label>
                <input
                  id={`oncall-callback-number-${call.id}`}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  placeholder="(555) 555-0142"
                  value={number}
                  disabled={busy}
                  onChange={(e) => { setNumber(e.target.value); setError(null); }}
                  className={`${fieldCls} min-h-[44px] w-full tabular-nums sm:min-h-0`}
                  data-testid="oncall-callback-number"
                />
              </div>
            )}
          </fieldset>

          {error && (
            <p
              role="alert"
              data-testid="oncall-callback-error"
              className="flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </p>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 dark:border-gray-700 sm:flex-row sm:justify-end">
            <button type="button" className={`${secondaryBtnCls} justify-center`} onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className={`${primaryBtnCls} justify-center`} disabled={busy} data-testid="oncall-callback-submit">
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <PhoneOutgoing className="h-4 w-4" aria-hidden="true" />}
              {busy ? 'Ringing your phone…' : 'Ring my phone'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CallbackButton;
